import { ROUTINE_EVENT } from '../../common/constants/VelarisSupportConstants';
import {
  getAllTickets,
  fetchAllParticipants,
  getTicket,
  fetchAllMessages,
  markTicketAsSeen,
  fetchAllTicketPriorities,
  fetchAllTicketStatuses,
  patchTicket,
  fetchTicketHistory,
  loadMoreTicketHistory,
  fetchTicketsV2Summary,
  updateTicketsListRefreshTs,
  fetchTicketsByEntityV2,
  clearTicketsByEntityV2,
  loadMoreTicketsByEntityV2,
  createMsgForTicket,
  uploadTicketAttachment,
} from '../routines/VelarisSupportRoutines';

export const updateStoreData = (key, action, state) => {
  const event = action.type.split('/')[1];
  switch (event) {
    case ROUTINE_EVENT.TRIGGER:
      return {
        ...state,
        [key]: {
          ...state[key],
          isLoading: true,
          isError: false,
        },
      };
    case ROUTINE_EVENT.SUCCESS:
      return {
        ...state,
        [key]: {
          ...state[key],
          isLoading: false,
          isError: false,
          data: action.payload,
        },
      };
    case ROUTINE_EVENT.FAILURE:
      return {
        ...state,
        [key]: {
          ...state[key],
          isLoading: false,
          isError: true,
          error: action.payload,
        },
      };
    default:
      return state;
  }
};

const STORE_ATTRS = {
  TICKETS: 'tickets',
  PARTICIPANTS: 'participants',
  ACTIVE_TICKET: 'activeTicket',
  TICKET_ACTIVITY: 'ticketActivity',
  TICKET_MESSAGES: 'ticketMessages',
  TICKET_STATUSES: 'ticketStatuses',
  TICKET_PRIORITIES: 'ticketPriorities',
  TICKET_SUMMARY: 'ticketSummary',
  TICKETS_BY_ENTITY: 'ticketsByEntity',
};

const initialState = {
  [STORE_ATTRS.TICKETS]: {
    isLoading: false,
    isError: false,
    error: null,
    data: {
      tickets: [],
    },
  },
  [STORE_ATTRS.PARTICIPANTS]: {
    isLoading: false,
    isError: false,
    error: null,
    data: [],
  },
  [STORE_ATTRS.TICKETS_BY_ENTITY]: {
    isLoading: false,
    isError: false,
    isLoadingMore: false,
    error: null,
    data: {
      tickets: [],
    },
  },
  [STORE_ATTRS.ACTIVE_TICKET]: {
    isLoading: false,
    isUpdating: false,
    isError: false,
    error: null,
    data: {},
  },
  [STORE_ATTRS.TICKET_ACTIVITY]: {
    isLoading: false,
    isError: false,
    error: null,
    isLoadingMore: false,
    data: {
      history: {},
    },
  },
  [STORE_ATTRS.TICKET_MESSAGES]: {
    isLoading: false,
    isSending: false,
    isUploading: false,
    isError: false,
    error: null,
    data: {
      messages: [],
    },
  },
  [STORE_ATTRS.TICKET_SUMMARY]: {
    isLoading: false,
    isError: false,
    error: null,
    data: {
      ticketStatusSummary: {
        NEW: 0,
        OPEN: 0,
        PENDING: 0,
        HOLD: 0,
        SOLVED: 0,
        CLOSED: 0,
      },
      ticketPrioritySummary: {
        URGENT: 0,
        HIGH: 0,
        MEDIUM: 0,
        LOW: 0,
      },
    },
  },
  [STORE_ATTRS.TICKET_STATUSES]: [],
  [STORE_ATTRS.TICKET_PRIORITIES]: [],
  ticketsListRefreshedTs: null,
  sendingNewMessage: false,
};

const velarisSupportReducer = (state = initialState, action) => {
  switch (action.type) {
    case getAllTickets.TRIGGER:
    case getAllTickets.SUCCESS:
    case getAllTickets.FAILURE:
      return updateStoreData(STORE_ATTRS.TICKETS, action, state);
    case fetchTicketsByEntityV2.TRIGGER:
    case fetchTicketsByEntityV2.SUCCESS:
    case fetchTicketsByEntityV2.FAILURE:
      return updateStoreData(STORE_ATTRS.TICKETS_BY_ENTITY, action, state);
    case fetchAllParticipants.TRIGGER:
    case fetchAllParticipants.SUCCESS:
    case fetchAllParticipants.FAILURE:
      return updateStoreData(STORE_ATTRS.PARTICIPANTS, action, state);
    case getTicket.TRIGGER:
    case getTicket.SUCCESS:
    case getTicket.FAILURE:
      return updateStoreData(STORE_ATTRS.ACTIVE_TICKET, action, state);
    case fetchAllMessages.TRIGGER:
    case fetchAllMessages.SUCCESS:
    case fetchAllMessages.FAILURE:
      return updateStoreData(STORE_ATTRS.TICKET_MESSAGES, action, state);
    case fetchTicketHistory.TRIGGER:
    case fetchTicketHistory.SUCCESS:
    case fetchTicketHistory.FAILURE:
      return updateStoreData(STORE_ATTRS.TICKET_ACTIVITY, action, state);
    case fetchTicketsV2Summary.TRIGGER:
    case fetchTicketsV2Summary.SUCCESS:
    case fetchTicketsV2Summary.FAILURE:
      return updateStoreData(STORE_ATTRS.TICKET_SUMMARY, action, state);
    case fetchAllTicketStatuses.SUCCESS:
      return {
        ...state,
        [STORE_ATTRS.TICKET_STATUSES]: {
          ...state[STORE_ATTRS.TICKET_STATUSES],
          ...action.payload,
        },
      };
    case fetchAllTicketPriorities.SUCCESS:
      return {
        ...state,
        [STORE_ATTRS.TICKET_PRIORITIES]: {
          ...state[STORE_ATTRS.TICKET_PRIORITIES],
          ...action.payload,
        },
      };
    case markTicketAsSeen.SUCCESS:
      return {
        ...state,
        [STORE_ATTRS.TICKETS]: {
          ...state[STORE_ATTRS.TICKETS],
          data: {
            ...state[STORE_ATTRS.TICKETS].data,
            tickets: state[STORE_ATTRS.TICKETS].data.tickets.map((ticket) =>
              ticket.ticketId === parseInt(action.payload.ticketId, 10)
                ? { ...ticket, seen: 1 }
                : ticket
            ),
          },
        },
      };
    case patchTicket.TRIGGER:
      return {
        ...state,
        [STORE_ATTRS.ACTIVE_TICKET]: {
          ...state[STORE_ATTRS.ACTIVE_TICKET],
          isUpdating: true,
        },
        [STORE_ATTRS.TICKETS]: {
          ...state[STORE_ATTRS.TICKETS],
          isLoading: true,
        },
      };
    case patchTicket.SUCCESS:
      return {
        ...state,
        [STORE_ATTRS.ACTIVE_TICKET]: {
          ...state[STORE_ATTRS.ACTIVE_TICKET],
          isUpdating: false,
          data: action.payload.data,
        },
        [STORE_ATTRS.TICKETS]: {
          ...state[STORE_ATTRS.TICKETS],
          data: {
            ...state[STORE_ATTRS.TICKETS].data,
            tickets: state[STORE_ATTRS.TICKETS].data.tickets.map((ticket) =>
              ticket.ticketId === action.payload.data.ticketId
                ? { ...ticket, ...action.payload.data }
                : ticket
            ),
          },
        },
        ticketsListRefreshedTs: new Date().getTime(),
      };
    case patchTicket.FAILURE:
      return {
        ...state,
        [STORE_ATTRS.ACTIVE_TICKET]: {
          ...state[STORE_ATTRS.ACTIVE_TICKET],
          isUpdating: false,
          isError: true,
          error: action.payload.error,
        },
        [STORE_ATTRS.TICKETS]: {
          ...state[STORE_ATTRS.TICKETS],
          isLoading: false,
        },
      };
    case loadMoreTicketHistory.TRIGGER:
      return {
        ...state,
        [STORE_ATTRS.TICKET_ACTIVITY]: {
          ...state[STORE_ATTRS.TICKET_ACTIVITY],
          isLoadingMore: true,
        },
      };
    case loadMoreTicketHistory.SUCCESS:
      return {
        ...state,
        [STORE_ATTRS.TICKET_ACTIVITY]: {
          ...state[STORE_ATTRS.TICKET_ACTIVITY],
          isLoadingMore: false,
          data: {
            ...state[STORE_ATTRS.TICKET_ACTIVITY].data,
            page: action.payload.page,
            history: {
              ...state[STORE_ATTRS.TICKET_ACTIVITY].data.history,
              ...action.payload.history,
            },
          },
        },
      };
    case loadMoreTicketHistory.FAILURE:
      return {
        ...state,
        [STORE_ATTRS.TICKET_ACTIVITY]: {
          ...state[STORE_ATTRS.TICKET_ACTIVITY],
          isLoadingMore: false,
          isError: true,
          error: action.payload.error,
        },
      };
    case updateTicketsListRefreshTs.TRIGGER:
      return {
        ...state,
        ticketsListRefreshedTs: new Date().getTime(),
      };
    case clearTicketsByEntityV2.TRIGGER:
      return {
        ...state,
        [STORE_ATTRS.TICKETS_BY_ENTITY]: {
          isLoading: false,
          isError: false,
          isLoadingMore: false,
          error: null,
          data: {
            tickets: [],
          },
        },
      };
    case loadMoreTicketsByEntityV2.TRIGGER:
      return {
        ...state,
        [STORE_ATTRS.TICKETS_BY_ENTITY]: {
          ...state[STORE_ATTRS.TICKETS_BY_ENTITY],
          isLoadingMore: true,
        },
      };
    case loadMoreTicketsByEntityV2.SUCCESS:
      return {
        ...state,
        [STORE_ATTRS.TICKETS_BY_ENTITY]: {
          ...state[STORE_ATTRS.TICKETS_BY_ENTITY],
          isLoadingMore: false,
          data: {
            ...state[STORE_ATTRS.TICKETS_BY_ENTITY].data,
            page: action.payload.page,
            tickets: [
              ...state[STORE_ATTRS.TICKETS_BY_ENTITY].data.tickets,
              ...action.payload.tickets,
            ],
          },
        },
      };
    case createMsgForTicket.TRIGGER:
      return {
        ...state,
        [STORE_ATTRS.TICKET_MESSAGES]: {
          ...state[STORE_ATTRS.TICKET_MESSAGES],
          isSending: true,
        },
      };
    case createMsgForTicket.SUCCESS:
      return {
        ...state,
        [STORE_ATTRS.TICKET_MESSAGES]: {
          ...state[STORE_ATTRS.TICKET_MESSAGES],
          isSending: false,
          data: {
            ...state[STORE_ATTRS.TICKET_MESSAGES].data,
            page: action.payload.page,
            messages: [
              ...state[STORE_ATTRS.TICKET_MESSAGES].data.messages,
              ...[action.payload.data],
            ],
          },
        },
      };
    case uploadTicketAttachment.TRIGGER:
      return {
        ...state,
        [STORE_ATTRS.TICKET_MESSAGES]: {
          ...state[STORE_ATTRS.TICKET_MESSAGES],
          isUploading: true,
        },
      };
    case uploadTicketAttachment.SUCCESS:
      return {
        ...state,
        [STORE_ATTRS.TICKET_MESSAGES]: {
          ...state[STORE_ATTRS.TICKET_MESSAGES],
          isUploading: false,
        },
      };
    default:
      return state;
  }
};

export default velarisSupportReducer;
